import { useApi, PaginatedResponse } from '@/api';
import {
  CurrencyValue,
  DataTable,
  WalletAddress,
  RelativeDateTime,
  TableSkeleton,
  useTableProps
} from '@blockanalitica/ui';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import { Link } from '@/components';

type LiquidatorsData = {
  liquidator_address: string;
  last_active: string;
  liquidations: string | number | null;
  total_collateral: string;
  total_debt: string;
  total_profits: string | null;
  share: string;
};

export default function LiquidatorsTable() {
  const { pagination, sorting } = useTableProps({
    pageSize: 10,
    initialSorting: '-total_collateral_seized_usd',
    filtersDebounceTime: 500
  });
  const [data, error] = useApi<PaginatedResponse<LiquidatorsData[]>>({
    url: `/liquidations/liquidators-table/`,
    queryParams: {
      page: pagination.currentPage,
      limit: pagination.pageSize,
      sort: sorting.currentSorting
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={10} columns={6} />;
  }

  const columns: TableColumn[] = [
    {
      header: 'Liquidators Address',
      getCell: (row) => (
        <Link
          key={row.liquidator_address}
          to={`${row.liquidator_address}`}
          relative="path">
          <WalletAddress address={row.liquidator_address} />
        </Link>
      )
    },
    {
      header: 'Liquidations',
      getCell: (row) => <CurrencyValue value={row.number_of_liquidations} />,
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'number_of_liquidations'
    },
    {
      header: 'Total Collateral Liquidated',
      getCell: (row) => (
        <CurrencyValue
          value={row.total_collateral_seized_usd}
          options={{
            currency: 'USD'
          }}
        />
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'total_collateral_seized_usd'
    },
    {
      header: 'Total Debt Repaid',
      getCell: (row) => (
        <CurrencyValue
          value={row.total_debt_repaid_usd}
          options={{
            currency: 'USD'
          }}
        />
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'total_debt_repaid_usd'
    },
    {
      header: 'Total Profits',
      getCell: (row) => (
        <CurrencyValue
          value={row.total_profits}
          options={{
            currency: 'USD'
          }}
        />
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'total_profits'
    },
    {
      header: 'Last Activity',
      getCell: (row) => <RelativeDateTime value={row.last_activity} />,
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'last_activity'
    }
  ];

  return (
    <DataTable
      rows={data['results']}
      columns={columns}
      getKey={(row) => `${row.liquidator_address}`}
      sorting={sorting}
      pagination={{
        ...pagination,
        totalRecords: data.pagination.total
      }}
    />
  );
}
